import React, { useEffect, useState } from 'react';
import { Link } from '../components/LocalizedLink';
import { Phone, Clock, Shield, MapPin, Star, ChevronRight, Zap } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import { useTranslation } from 'react-i18next';
import { logEvent } from '../lib/analytics';
import { SEO } from '../lib/seo';
import 'swiper/css';
import 'swiper/css/pagination';

interface GoogleReview {
  author_name: string;
  rating: number;
  text: string;
  time: number;
  profile_photo_url: string;
}

function Home() {
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    // Load Featurable script
    const script = document.createElement('script');
    script.src = 'https://featurable.com/assets/bundle.js';
    script.defer = true;
    script.charset = 'UTF-8';
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleCallClick = () => {
    logEvent('Contact', 'Click', 'Hero Call Button');
  };

  const locations = {
    barcelones: [
      'Barcelona',
      'Badalona',
      'Santa Coloma de Gramenet',
      'Sant Adrià de Besòs',
      'L\'Hospitalet de Llobregat'
    ],
    baixLlobregat: [
      'Abrera',
      'Begues',
      'Castelldefels',
      'Castellví de Rosanes',
      'Cervelló',
      'Collbató',
      'Corbera de Llobregat',
      'Cornellà de Llobregat',
      'Esparreguera',
      'Esplugues de Llobregat',
      'Gavà',
      'Martorell',
      'Molins de Rei',
      'Olesa de Montserrat',
      'Pallejà',
      'la Palma de Cervelló',
      'el Papiol',
      'el Prat de Llobregat',
      'Sant Andreu de la Barca',
      'Sant Boi de Llobregat',
      'Sant Climent de Llobregat',
      'Sant Esteve Sesrovires',
      'Sant Feliu de Llobregat',
      'Sant Joan Despí',
      'Sant Just Desvern',
      'Sant Vicenç dels Horts',
      'Santa Coloma de Cervelló',
      'Torrelles de Llobregat',
      'Vallirana',
      'Viladecans'
    ],
    garraf: [
      'Canyelles',
      'Cubelles',
      'Olivella',
      'Sant Pere de Ribes',
      'Sitges',
      'Vilanova i la Geltrú'
    ]
  };

  const services = [
    {
      title: t('services.emergency.title'),
      description: t('services.emergency.description'),
      link: '/emergency-repairs'
    },
    {
      title: t('services.installations.title'),
      description: t('services.installations.description'),
      link: '/electrical-installations'
    },
    {
      title: t('services.inspections.title'),
      description: t('services.inspections.description'),
      link: '/safety-inspections'
    },
    {
      title: t('services.maintenance.title'),
      description: t('services.maintenance.description'),
      link: '/maintenance'
    },
    {
      title: t('services.cie.title'),
      description: t('services.cie.description'),
      link: '/cie'
    },
    {
      title: t('services.brie.title'),
      description: t('services.brie.description'),
      link: '/brie'
    }
  ];

  return (
    <>
      <SEO 
        title="Electricista Urgente 24h Barcelona | Servicio Inmediato"
        description="Electricista autorizado 24 horas en Barcelona. Servicio de urgencias, instalaciones y reparaciones eléctricas. Respuesta en menos de 30 minutos. ☎ +34 600 23 82 83"
        canonical="/"
        type="website"
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <header className="bg-black text-white relative w-full">
          <div className="absolute inset-0 overflow-hidden z-0">
            <img 
              src="https://titulae.es/wp-content/uploads/2023/11/que-hace-un-electricista.jpg" 
              alt={t('hero.imageAlt')}
              className="w-full h-full object-cover transform scale-x-[-1]"
            />
            <div className="absolute inset-0 bg-black bg-opacity-60"></div>
          </div>
          <div className="relative z-10 container mx-auto section-padding">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
              <div className="lg:w-1/2">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  {t('hero.title')}
                  <span className="text-yellow-500"> Barcelona</span>
                </h1>
                <p className="text-xl mb-8">
                  {t('hero.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="tel:+34600238283" 
                    className="btn-primary flex items-center justify-center gap-2"
                    onClick={handleCallClick}
                  >
                    <Phone className="w-5 h-5" />
                    {t('hero.callNow')}: +34 600 23 82 83
                  </a>
                  <a href="#services" className="bg-white text-black hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2">
                    {t('hero.ourServices')} <ChevronRight className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Features */}
        <section className="bg-gray-50 section-padding">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <Clock className="w-12 h-12 text-yellow-500 mb-4" />
                <h3 className="text-xl font-bold mb-2">{t('features.service24h.title')}</h3>
                <p>{t('features.service24h.description')}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <Zap className="w-12 h-12 text-yellow-500 mb-4" />
                <h3 className="text-xl font-bold mb-2">{t('features.immediateResponse.title')}</h3>
                <p>{t('features.immediateResponse.description')}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <Shield className="w-12 h-12 text-yellow-500 mb-4" />
                <h3 className="text-xl font-bold mb-2">{t('features.licensed.title')}</h3>
                <p>{t('features.licensed.description')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section id="services" className="section-padding">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">{t('services.title')}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <Link 
                  key={index} 
                  to={service.link}
                  className="border p-6 rounded-lg hover:border-yellow-500 transition-colors"
                >
                  <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <span className="text-yellow-500 font-semibold flex items-center gap-2">
                    {t('common.moreInfo')} <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="bg-gray-50 section-padding">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">{t('reviews.title')}</h2>
            <div 
              id="featurable-82355af8-ba73-4485-bb53-cb941a61aed6" 
              data-featurable-async
              className="mb-8"
            ></div>
          </div>
        </section>

        {/* Service Area */}
        <section className="section-padding">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">{t('serviceArea.title')}</h2>
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="lg:w-1/2">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2992.000860016895!2d2.1818915!3d41.417500000000004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4a2fc0b344977%3A0x96f8bb4ba192a30d!2sElectricista%20Lampista%20Urgencias%2024h!5e0!3m2!1ses!2ses!4v1739378142502!5m2!1ses!2ses"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-lg shadow-lg"
                ></iframe>
              </div>
              <div className="lg:w-1/2">
                <h3 className="text-2xl font-bold mb-6">{t('serviceArea.subtitle')}</h3>
                <div className="grid gap-8">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-yellow-500">{t('serviceArea.barcelones')}</h4>
                    <ul className="grid grid-cols-2 gap-3">
                      {locations.barcelones.map((area, index) => (
                        <li key={index}>
                          <a
                            href="https://maps.app.goo.gl/rQw99Xgv2xom5nvM7"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 hover:text-yellow-500 transition-colors"
                          >
                            <MapPin className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                            <span>{area}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-yellow-500">{t('serviceArea.baixLlobregat')}</h4>
                    <ul className="grid grid-cols-2 gap-3">
                      {locations.baixLlobregat.map((area, index) => (
                        <li key={index}>
                          <a
                            href="https://maps.app.goo.gl/rQw99Xgv2xom5nvM7"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 hover:text-yellow-500 transition-colors"
                          >
                            <MapPin className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                            <span>{area}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-yellow-500">{t('serviceArea.garraf')}</h4>
                    <ul className="grid grid-cols-2 gap-3">
                      {locations.garraf.map((area, index) => (
                        <li key={index}>
                          <a
                            href="https://maps.app.goo.gl/rQw99Xgv2xom5nvM7"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 hover:text-yellow-500 transition-colors"
                          >
                            <MapPin className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                            <span>{area}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Home;